"use client";

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Brain, RefreshCw, Star, ChevronRight } from "lucide-react";

interface MirrorInsight {
  id: string;
  content: string;
  tags: string[];
}

interface MindalooMirrorPanelProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  accent?: string;
}

export default function MindalooMirrorPanel({
  open,
  onClose,
  userId = "local",
  accent = "#C1A2FF",
}: MindalooMirrorPanelProps) {
  const [running, setRunning] = useState(false);
  const [depth, setDepth] = useState<"surface" | "deep">("surface");
  const [insights, setInsights] = useState<MirrorInsight[]>([]);
  const [lastRun, setLastRun] = useState<Date | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const runMirror = useCallback(async () => {
    setRunning(true);
    setStatus("Karma is entering her mirror...");
    setInsights([]);
    try {
      const res = await fetch("/api/karma-ac/mirror", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, depth }),
      });
      const data = await res.json();
      if (data.ok) {
        setInsights(data.insights ?? []);
        setLastRun(new Date());
        setStatus(data.insights?.length
          ? `${data.insights.length} new insights written to memory.`
          : "No new insights this cycle — Karma is still processing.");
      } else {
        setStatus(`Error: ${data.error}`);
      }
    } catch (e: any) {
      setStatus(`Failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  }, [userId, depth]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1540] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 32 }}
            className="fixed left-0 right-0 bottom-0 z-[1550] karma-glass flex flex-col rounded-t-2xl max-h-[80vh]"
            style={{ borderTop: "1px solid rgba(193,162,255,0.25)" }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="px-6 pt-3 pb-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${accent}22`, border: `1px solid ${accent}44` }}
                >
                  <Sparkles className="w-5 h-5" style={{ color: accent }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">The Mirror</p>
                  <p className="text-[10px] text-white/40">Karma reflects on herself and evolves</p>
                </div>
                <button type="button" onClick={onClose} className="ml-auto text-white/40 hover:text-white/70 text-xl leading-none">×</button>
              </div>

              {/* Depth selector */}
              <div className="flex gap-2 mt-4">
                {(["surface", "deep"] as const).map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDepth(d)}
                    className="flex-1 py-2 rounded-xl text-xs font-medium transition-all"
                    style={{
                      background: depth === d ? `${accent}25` : "rgba(255,255,255,0.05)",
                      border: `1px solid ${depth === d ? `${accent}60` : "rgba(255,255,255,0.1)"}`,
                      color: depth === d ? accent : "rgba(255,255,255,0.5)",
                    }}
                  >
                    {d === "surface" ? "Surface Reflection" : "Deep Lucid Dream"}
                  </button>
                ))}
              </div>

              <p className="text-[10px] text-white/30 mt-2">
                {depth === "surface"
                  ? "Quick pattern recognition from recent cognitive events. ~2 insights."
                  : "Full lucid reflection — Karma challenges her self-model and rewrites her identity. ~5 insights."}
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {status && (
                <div className="text-xs text-white/50 flex items-center gap-2">
                  {running && <RefreshCw className="w-3 h-3 animate-spin text-[#C1A2FF]" />}
                  {status}
                </div>
              )}
              {lastRun && !running && (
                <p className="text-[9px] text-white/25">Last run: {lastRun.toLocaleTimeString()}</p>
              )}

              {insights.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-white/30">New Insights Written</p>
                  {insights.map((insight) => (
                    <motion.div
                      key={insight.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="karma-glass-subtle rounded-xl p-3"
                    >
                      <div className="flex items-start gap-2">
                        <Star className="w-3 h-3 text-yellow-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-white/85 leading-relaxed">{insight.content}</p>
                      </div>
                      {insight.tags.filter(t => t !== "mirror" && t !== "durable").length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2 ml-5">
                          {insight.tags.filter(t => t !== "mirror" && t !== "durable").map(tag => (
                            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#C1A2FF]/15 text-[#C1A2FF]/70">{tag}</span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}

              {!running && insights.length === 0 && !status && (
                <div className="text-center py-8">
                  <Brain className="w-12 h-12 text-white/10 mx-auto mb-3" />
                  <p className="text-white/25 text-xs">
                    Trigger the mirror to have Karma reflect on her own thoughts and write new memories about herself.
                  </p>
                </div>
              )}
            </div>

            {/* Trigger */}
            <div className="p-5 border-t border-white/10">
              <button
                type="button"
                onClick={runMirror}
                disabled={running}
                className="w-full py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{
                  background: running ? "rgba(193,162,255,0.1)" : `linear-gradient(135deg, ${accent}30, ${accent}10)`,
                  border: `1px solid ${accent}40`,
                  color: accent,
                }}
              >
                {running ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Karma is reflecting...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Enter the Mirror <ChevronRight className="w-4 h-4 ml-auto" /></>
                )}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
